import React, { FC, useEffect, useState } from 'react';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import { useIntl } from 'react-intl';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import { useDispatch } from 'react-redux';
import { useDataProvider, getDayStart, getDayEnd } from 'src/app/common/utils';
import {
  fetchCampaignList,
  CampaignListParam,
  getCampaignCategoryList,
} from 'src/app/modules/PulseLeads/network/campaignCrud';
import { PaginateList } from 'src/app/common/types/common-types';
import { CampaignItem, CampaignStatusEnum } from 'src/app/modules/PulseLeads/types/campaign-types';
import { map } from 'lodash';
import CampaignList from './components/CampaignList';
import { useLang } from 'src/app/i18n';

type CampaignListingPageProps = {} & ParamsProps;

const initialState: CampaignListParam = {
  name: '',
  startDateFrom: null,
  startDateTo: null,
  endDateFrom: null,
  endDateTo: null,
  campaignCategory: undefined, //PHKL Custom
  sort: '',
  status: '',
  limit: 5,
  page: 1,
};

export const campaignFilterKeys = [
  'name',
  'startDateFrom',
  'startDateTo',
  'endDateFrom',
  'endDateTo',
  'sort',
  'status',
  'campaignCategory', //PHKL Custom
];

const paramsInitiator = (initialParams?: Record<string, string>): CampaignListParam => {
  return initialParams
    ? {
        name: initialParams.name || '',
        startDateFrom: initialParams.startDateFrom ? getDayStart(new Date(initialParams.startDateFrom)) : null,
        startDateTo: initialParams.startDateTo ? getDayEnd(new Date(initialParams.startDateTo)) : null,
        endDateFrom: initialParams.endDateFrom ? getDayStart(new Date(initialParams.endDateFrom)) : null,
        endDateTo: initialParams.endDateTo ? getDayEnd(new Date(initialParams.endDateTo)) : null,
        campaignCategory: initialParams.campaignCategory || undefined, //PHKL Custom
        sort: initialParams.sort || '',
        status: initialParams.status || '',
        limit: 5,
        page: 1,
      }
    : initialState;
};

const CampaignListingPage: FC<CampaignListingPageProps> = ({ initialParams, onChangeQueryParams }) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();
  const lang = useLang(); //PHKL Custom

  const [filterState, setFilterState] = useState<CampaignListParam>(paramsInitiator(initialParams));
  const [campaignList, setCampaignList] = useState<PaginateList<CampaignItem>>();
  const [campaignCategoryList, setCampaignCategoryList] = useState<{ displayName: string; value: string }[]>(); //PHKL Custom

  const { isLoading, refreshData } = useDataProvider<PaginateList<CampaignItem>>(
    async () => {
      try {
        if (onChangeQueryParams) {
          onChangeQueryParams(filterState);
        }
        return await fetchCampaignList(filterState, dispatch);
      } catch (err) {}
    },
    setCampaignList,
    false,
  );

  //PHKL Custom - s
  useEffect(() => {
    getCampaignCategoryList(lang).then((result) => {
      const optionList: { displayName: string; value: string }[] = [{ displayName: '', value: '' }];
      result.map((item) => {
        return optionList.push({ displayName: item.name, value: item._id });
      });
      setCampaignCategoryList(optionList);
    });
  }, [lang]);
  //PHKL Custom - e

  return (
    <>
      <PruFilter
        title={Translation('pulseleads.campaign.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'name',
            initialValue: filterState.name,
            displayName: Translation('pulseleads.campaign.common.name'),
          },
          // {
          //   type: PruFilterItemType.DROPDOWN,
          //   field: "sort",
          //   initialValue: filterState.sort,
          //   displayName: Translation("pulseleads.campaign.common.sort"),
          //   choices: [
          //     {displayName: "", value: ""},
          //     ...map(CampaignSortEnum, (option: string) => ({ displayName: Translation(`pulseleads.campaign.sort.option.${option}`), value: option }))
          //   ]
          // },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'status',
            initialValue: filterState.status,
            displayName: Translation('pulseleads.campaign.common.status'),
            choices: [
              { displayName: '', value: '' },
              ...map(CampaignStatusEnum, (option: string) => ({
                displayName: Translation(`pulseleads.campaign.status.option.${option}`),
                value: option,
              })),
            ],
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'startDateFrom',
            fieldTo: 'startDateTo',
            initialDateFrom: filterState.startDateFrom,
            initialDateTo: filterState.startDateTo,
            displayName: Translation('pulseleads.campaign.common.startDate'),
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'endDateFrom',
            fieldTo: 'endDateTo',
            initialDateFrom: filterState.endDateFrom,
            initialDateTo: filterState.endDateTo,
            displayName: Translation('pulseleads.campaign.common.endDate'),
          },
          //PHKL Custom - s
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'campaignCategory',
            initialValue: filterState.campaignCategory,
            displayName: Translation('pulseleads.campaign.common.campaignCategory'),
            choices: campaignCategoryList ? campaignCategoryList : [{ displayName: '', value: '' }],
          },
          //PHKL Custom - e
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            name: data.name,
            startDateFrom: data.startDateFrom,
            startDateTo: data.startDateTo,
            endDateFrom: data.endDateFrom,
            endDateTo: data.endDateTo,
            sort: data.sort,
            status: data.status,
            campaignCategory: data.campaignCategory, //PHKL Custom
          })
        }
        fetchData={refreshData}
      />
      <CampaignList
        isLoading={isLoading}
        campaignList={campaignList}
        campaignCategoryList={campaignCategoryList}
        onRefresh={refreshData}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default CampaignListingPage;
